import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import {
  ChangeReviewQuestionMutationMutation,
  ChangeReviewQuestionMutationMutationVariables,
} from "types/graphql-schema";

import changeReviewQuestionMutation from "@apps/programs/graphql/change-review-question-mutation";
import getReviewStepQuestionsQuery from "@apps/programs/graphql/get-review-step-questions-query";
import { useProgramsSection } from "@apps/programs/programs-section-context";
import { successNotificationVar } from "@cache/cache";
import useConfirm from "@helpers/hooks/use-confirm";

import { ReviewQuestionScope } from "./review-question-rows";

type ChangeVariables = Omit<
  ChangeReviewQuestionMutationMutationVariables,
  "complianceProgramId" | "assessmentTemplateId"
>;

/**
 * Runs one question change on a published review, after a confirmation when one is given.
 *
 * The rules live in the backend (compliance/question_changes.py). A refusal comes back as
 * the mutation's error and shows as the usual error toast; the expected ones get a plain
 * title here, which also keeps them out of Sentry.
 */
const useChangeReviewQuestion = (review: ReviewQuestionScope) => {
  const { noun } = useProgramsSection();
  const nounLower = noun.toLowerCase();
  const [changeReviewQuestion, { loading }] = useMutation<
    ChangeReviewQuestionMutationMutation,
    ChangeReviewQuestionMutationMutationVariables
  >(changeReviewQuestionMutation);
  const { ConfirmationDialog, confirm, setTitle, setMessage } = useConfirm(
    "",
    "",
  );

  const change = useCallback(
    async (
      variables: ChangeVariables,
      {
        successTitle,
        confirmation,
      }: {
        successTitle: string;
        confirmation?: { title: string; message: string };
      },
    ) => {
      if (confirmation) {
        setTitle(confirmation.title);
        setMessage(confirmation.message);
        if (!(await confirm())) {
          return false;
        }
      }
      return new Promise<boolean>((resolve) => {
        changeReviewQuestion({
          variables: { ...review, ...variables },
          // The Questions panel, the talent indicators row and the preview read the change from this
          // query. The edit form copies its program query into state once, so refetching that query
          // would change nothing. The change is saved once the mutation answers: a failed refetch shows
          // in the panel, not as a failed save.
          refetchQueries: [getReviewStepQuestionsQuery],
          awaitRefetchQueries: false,
          context: {
            errorMatches: [
              {
                match: "Only an admin can change a question everywhere",
                title: "Only an admin can fix a question everywhere",
                description: `Choose "Only in this ${nounLower}" instead.`,
              },
              {
                match: "A step keeps at least one question",
                title: "A step keeps at least one question",
              },
              {
                match: "A question title can be at most",
                title: "This title is too long",
              },
              {
                match: "must be reopened before its questions can change",
                title: `Reopen this ${nounLower} to change its questions`,
              },
              {
                match: "You do not have permission to change the questions",
                title: `You cannot change the questions of this ${nounLower}`,
              },
            ],
          },
          onCompleted: (data) => {
            // Nothing to say when it was already so: no row moved.
            if (data.changeReviewQuestion?.changed) {
              successNotificationVar({ title: successTitle, timeout: 2000 });
            }
            resolve(true);
          },
          // The error link already shows the toast.
          onError: () => resolve(false),
        });
      });
    },
    [changeReviewQuestion, confirm, nounLower, review, setMessage, setTitle],
  );

  return { change, loading, ConfirmationDialog };
};

export default useChangeReviewQuestion;
